import { CalendarDays, CheckCircle2, FileText, ShieldCheck, TriangleAlert } from 'lucide-react';

import type { RuntimeAnswer } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function asList(value: unknown): UnknownRecord[] {
  return Array.isArray(value)
    ? value.filter((item): item is UnknownRecord => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
    : [];
}

function stringValue(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function label(value: string | null | undefined): string | null {
  return value ? value.replace(/_/g, ' ') : null;
}

function sentenceLabel(value: string | null | undefined): string | null {
  const normalized = label(value);
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : null;
}

function unique(values: Array<string | null>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function extractDeadlinePacket(answer: RuntimeAnswer): UnknownRecord {
  const synthesis = asRecord(answer.court_deadline_synthesis_packet);
  const packet = asRecord(synthesis.deadline_computation);
  return Object.keys(packet).length > 0 ? packet : {};
}

function extractInputs(answer: RuntimeAnswer, packet: UnknownRecord): UnknownRecord {
  const synthesis = asRecord(answer.court_deadline_synthesis_packet);
  return asRecord(packet.inputs) || asRecord(synthesis.inputs);
}

function extractResult(packet: UnknownRecord): UnknownRecord {
  return asRecord(packet.result);
}

function extractAuthorityPackets(answer: RuntimeAnswer, packet: UnknownRecord): UnknownRecord[] {
  if (Array.isArray(answer.court_calendar_authority_packets)) {
    return answer.court_calendar_authority_packets;
  }
  return asList(packet.court_calendar_authority_packets);
}

function extractPrimaryAuthority(answer: RuntimeAnswer, packet: UnknownRecord): UnknownRecord {
  const answerAuthority = asRecord(answer.court_calendar_authority);
  if (Object.keys(answerAuthority).length > 0) {
    return answerAuthority;
  }
  const packetAuthority = asRecord(packet.court_calendar_authority);
  if (Object.keys(packetAuthority).length > 0) {
    return packetAuthority;
  }
  return extractAuthorityPackets(answer, packet)[0] ?? {};
}

function extractTraceContext(answer: RuntimeAnswer, packet: UnknownRecord): UnknownRecord {
  const synthesis = asRecord(answer.court_deadline_synthesis_packet);
  const answerTrace = asRecord(answer.trace_context);
  if (Object.keys(answerTrace).length > 0) {
    return answerTrace;
  }
  const synthesisTrace = asRecord(synthesis.trace_context);
  if (Object.keys(synthesisTrace).length > 0) {
    return synthesisTrace;
  }
  return asRecord(packet.trace_context);
}

function extractSkippedDates(result: UnknownRecord): UnknownRecord[] {
  return asList(result.calculation_trace).flatMap((step) => asList(step.skipped_dates));
}

function extractCountedDates(result: UnknownRecord): string[] {
  return unique(
    asList(result.calculation_trace).flatMap((step) => {
      const dates = step.counted_dates;
      return Array.isArray(dates)
        ? dates.map((date) => stringValue(date))
        : [];
    }),
  );
}

function sourceTitles(authority: UnknownRecord, closureRefs: UnknownRecord[]): string[] {
  const provenanceTitles = asList(authority.source_provenance).map((source) =>
    stringValue(source.title) || stringValue(source.citation),
  );
  const closureTitles = closureRefs.map((source) => stringValue(source.citation));
  return unique([...provenanceTitles, ...closureTitles]);
}

function calendarRowCount(packet: UnknownRecord): number {
  return asList(packet.calendar_days).length;
}

function hasCompleteCoverage(packet: UnknownRecord): boolean {
  return stringValue(packet.coverage_status) === 'complete';
}

function closureExceptionSummary(packet: UnknownRecord): string {
  const count = calendarRowCount(packet);
  if (count > 0) {
    return `${count} closure/exception row${count === 1 ? '' : 's'}`;
  }
  if (hasCompleteCoverage(packet)) {
    return 'Open by covered calendar authority; no closure row';
  }
  return 'No closure/exception row in packet';
}

function dateRangeLabel(packet: UnknownRecord): string | null {
  const range = asRecord(packet.date_range_checked);
  const start = stringValue(range.start);
  const end = stringValue(range.end);
  if (start && end && start !== end) {
    return `${start} to ${end}`;
  }
  return start || end;
}

function inferCoverageYear(inputs: UnknownRecord, authorityPackets: UnknownRecord[]): string | null {
  const explicitYear = stringValue(inputs.coverage_year);
  if (explicitYear) {
    return explicitYear;
  }
  const yearSources = [
    stringValue(inputs.trigger_date),
    stringValue(inputs.final_date),
    ...authorityPackets.flatMap((packet) => {
      const range = asRecord(packet.date_range_checked);
      return [stringValue(range.start), stringValue(range.end)];
    }),
  ];
  const firstDate = yearSources.find((value): value is string => Boolean(value && /^\d{4}-/.test(value)));
  return firstDate ? firstDate.slice(0, 4) : null;
}

function finalDateLabel({
  questionType,
  finalDate,
  triggerDate,
  coverageYear,
}: {
  questionType: string | null;
  finalDate: string | null;
  triggerDate: string | null;
  coverageYear: string | null;
}): string | null {
  if (questionType === 'calendar_coverage_check') {
    return coverageYear ? `Coverage year ${coverageYear}` : null;
  }
  if (!finalDate) {
    return null;
  }
  if (questionType === 'court_open_status') {
    return finalDate === triggerDate ? `Open court day ${finalDate}` : `Next court day ${finalDate}`;
  }
  if (questionType === 'statutory_response_deadline') {
    return `Response deadline ${finalDate}`;
  }
  return `Final deadline ${finalDate}`;
}

function triggerDateLabel(questionType: string | null): string {
  if (questionType === 'calendar_coverage_check') {
    return 'Coverage probe date';
  }
  if (questionType === 'court_open_status') {
    return 'Date checked';
  }
  return 'Trigger date';
}

function showBaseDays(questionType: string | null): boolean {
  return questionType !== 'calendar_coverage_check' && questionType !== 'court_open_status';
}

function DetailRow({ labelText, value }: { labelText: string; value: string | number | null }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900/70">
        {labelText}
      </dt>
      <dd className="mt-0.5 break-words font-mono text-xs text-emerald-950">{value}</dd>
    </div>
  );
}

function AuthorityPacketRow({ packet, index }: { packet: UnknownRecord; index: number }) {
  const sources = sourceTitles(packet, []);
  const range = dateRangeLabel(packet);
  return (
    <li className="rounded border border-emerald-100 bg-white/80 p-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-emerald-950">
        <span className="font-semibold">Packet {index + 1}</span>
        {range ? <span>{range}</span> : null}
        {stringValue(packet.coverage_status) ? (
          <span>{sentenceLabel(stringValue(packet.coverage_status))}</span>
        ) : null}
        <span>{closureExceptionSummary(packet)}</span>
      </div>
      {sources.length > 0 ? (
        <div className="mt-1 text-xs leading-relaxed text-emerald-900">{sources.join(', ')}</div>
      ) : null}
    </li>
  );
}

function StatusIcon({ canStateDeadline }: { canStateDeadline: boolean }) {
  if (canStateDeadline) {
    return <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-emerald-700" />;
  }
  return <TriangleAlert aria-hidden="true" className="h-4 w-4 text-amber-700" />;
}

export function CalendarDeadlineAnswerCard({ answer }: Props) {
  const packet = extractDeadlinePacket(answer);
  const inputs = extractInputs(answer, packet);
  const result = extractResult(packet);
  const authority = extractPrimaryAuthority(answer, packet);
  const authorityPackets = extractAuthorityPackets(answer, packet);
  const traceContext = extractTraceContext(answer, packet);
  const closureRefs = Array.isArray(answer.closure_source_refs)
    ? answer.closure_source_refs
    : asList(packet.closure_source_refs);
  const skippedDates = extractSkippedDates(result);
  const countedDates = extractCountedDates(result);
  const sourceLabels = unique([
    ...sourceTitles(authority, closureRefs),
    ...authorityPackets.flatMap((authorityPacket) => sourceTitles(authorityPacket, [])),
  ]);
  const finalDate = answer.final_date || stringValue(result.final_date);
  const status = answer.deadline_computation_status || stringValue(packet.status) || 'unknown';
  const canStateDeadline = answer.can_state_deadline === true || packet.can_state_deadline === true;
  const packetSchema = stringValue(packet.schema);
  const synthesis = asRecord(answer.court_deadline_synthesis_packet);
  const synthesisSchema = stringValue(synthesis.schema);
  const questionType = stringValue(synthesis.question_type);
  const triggerDate = stringValue(inputs.trigger_date);
  const coverageYear = inferCoverageYear(inputs, authorityPackets);
  const headlineDate = finalDateLabel({ questionType, finalDate, triggerDate, coverageYear });
  const coverageStatuses = unique([
    stringValue(authority.coverage_status),
    ...authorityPackets.map((authorityPacket) => stringValue(authorityPacket.coverage_status)),
  ]);
  const dateRanges = unique([dateRangeLabel(authority), ...authorityPackets.map(dateRangeLabel)]);
  const blockingGaps = Array.isArray(answer.blocking_gaps)
    ? answer.blocking_gaps
    : asList(packet.blocking_gaps).map((gap) => stringValue(gap)).filter((gap): gap is string => Boolean(gap));
  const calendarRowTotal = authorityPackets.length > 0
    ? authorityPackets.reduce((total, authorityPacket) => total + calendarRowCount(authorityPacket), 0)
    : calendarRowCount(authority);
  const hasInferredOpenPacket = authorityPackets.some(
    (authorityPacket) => calendarRowCount(authorityPacket) === 0 && hasCompleteCoverage(authorityPacket),
  );

  return (
    <article className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded bg-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-950">
          <CalendarDays aria-hidden="true" className="h-3.5 w-3.5" />
          Court Calendar
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-900">
          <StatusIcon canStateDeadline={canStateDeadline} />
          {label(status)}
        </span>
        {headlineDate ? <span className="text-xs text-emerald-900">{headlineDate}</span> : null}
        <span className="text-xs text-emerald-900">
          {authorityPackets.length} authority packet{authorityPackets.length === 1 ? '' : 's'}
        </span>
      </div>

      <p className="whitespace-pre-line leading-relaxed text-emerald-950">{answer.content}</p>

      <section className="mt-4 border-t border-emerald-200 pt-3" aria-label="Deadline provenance">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-emerald-950">
          <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 text-emerald-700" />
          <span>Deadline provenance</span>
        </div>

        <dl className="grid gap-3 rounded-md border border-emerald-200 bg-white/70 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <DetailRow labelText="Packet schema" value={packetSchema} />
          <DetailRow labelText="Synthesis schema" value={synthesisSchema} />
          <DetailRow labelText={triggerDateLabel(questionType)} value={triggerDate} />
          {questionType === 'calendar_coverage_check' ? (
            <DetailRow labelText="Coverage year" value={coverageYear} />
          ) : null}
          <DetailRow labelText="Calculation type" value={label(stringValue(inputs.calculation_type))} />
          {showBaseDays(questionType) ? (
            <DetailRow labelText="Base days" value={numberValue(inputs.base_days)} />
          ) : null}
          <DetailRow labelText="Jurisdiction" value={stringValue(inputs.jurisdiction_code)} />
          <DetailRow labelText="Court" value={stringValue(inputs.court_code)} />
          <DetailRow
            labelText="Correlation"
            value={stringValue(traceContext.request_correlation_id) || stringValue(traceContext.chat_session_id)}
          />
        </dl>

        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <section className="rounded-md border border-emerald-200 bg-white/70 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-emerald-950">
              <FileText aria-hidden="true" className="h-3.5 w-3.5 text-emerald-700" />
              <span>Authority packets</span>
            </div>
            <dl className="grid gap-2 sm:grid-cols-2">
              <DetailRow labelText="Coverage" value={coverageStatuses.join(', ') || stringValue(authority.coverage_status)} />
              <DetailRow labelText="Date range" value={dateRanges.join(', ')} />
              <DetailRow labelText="Closure/exception rows" value={calendarRowTotal} />
              <DetailRow labelText="Closure refs" value={closureRefs.length} />
            </dl>
            {hasInferredOpenPacket ? (
              <p className="mt-3 text-xs leading-relaxed text-emerald-900">
                Open court days may be inferred from complete calendar coverage when no closure or
                holiday row exists for the checked date.
              </p>
            ) : null}
            {authorityPackets.length > 0 ? (
              <>
                <ul className="mt-3 space-y-2">
                  {authorityPackets.map((authorityPacket, index) => (
                    <AuthorityPacketRow
                      key={`${stringValue(authorityPacket.court_code) ?? 'court'}-${dateRangeLabel(authorityPacket) ?? index}-${index}`}
                      packet={authorityPacket}
                      index={index}
                    />
                  ))}
                </ul>
                {sourceLabels.length > 0 ? (
                  <div className="mt-3 text-xs leading-relaxed text-emerald-900">
                    Source{sourceLabels.length === 1 ? '' : 's'}: {sourceLabels.join(', ')}
                  </div>
                ) : null}
              </>
            ) : sourceLabels.length > 0 ? (
              <ul className="mt-3 space-y-1 text-xs leading-relaxed text-emerald-900">
                {sourceLabels.map((sourceLabel) => (
                  <li key={sourceLabel}>{sourceLabel}</li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="rounded-md border border-emerald-200 bg-white/70 p-3">
            <div className="mb-2 text-xs font-semibold text-emerald-950">Date trace</div>
            {skippedDates.length > 0 ? (
              <ul className="space-y-1 text-xs leading-relaxed text-emerald-900">
                {skippedDates.map((date, index) => {
                  const dateLabel = stringValue(date.date) || `skipped-${index + 1}`;
                  const reason =
                    stringValue(date.display_name) ||
                    stringValue(date.authority_reason) ||
                    sentenceLabel(stringValue(date.reason)) ||
                    'not counted';
                  const closureType = label(stringValue(date.closure_type));
                  const shouldShowType = closureType && closureType.toLowerCase() !== reason.toLowerCase();
                  return (
                    <li key={`${dateLabel}-${index}`}>
                      {dateLabel}: {reason}
                      {shouldShowType ? ` (${closureType})` : ''}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-xs text-emerald-900">No skipped dates in the packet trace.</p>
            )}
            {countedDates.length > 0 ? (
              <p className="mt-3 text-xs text-emerald-900">
                Counted court dates: {countedDates.join(', ')}.
              </p>
            ) : null}
            {blockingGaps.length > 0 ? (
              <p className="mt-3 text-xs text-amber-800">
                Blocking gaps: {blockingGaps.map((gap) => label(gap) ?? gap).join(', ')}.
              </p>
            ) : null}
          </section>
        </div>
      </section>
    </article>
  );
}
